import { Injectable } from "@angular/core";

import { Offer } from "graphql/models/extended";
import {
  OfferSettings,
  OfferSettingsItem,
} from "graphql/models/generated";
import { MultiSelectValue } from "models/multi-select.model";
import { OfferSettingsItemType } from "models/offer-settings.model";

import { StoreService } from "./store.service";

const DEFAULT_VALUES = {
  CUSTOMER: {
    NR: "",
    NAME: "",
  },
  OFFER_NR: "NV", // not available initially
  OBJECT_NR: "NV", // not available initially
};

interface UserName {
  firstName: string;
  lastName: string;
}

@Injectable({
  providedIn: "root",
})
export class OfferTitleService {
  private userName!: UserName;

  readonly defaultOfferSettings: Partial<OfferSettings> = {
    prefix1: {
      type: OfferSettingsItemType.DATE,
      value: MultiSelectValue.DDMMYYYY,
    } as OfferSettingsItem,
    prefix2: {
      type: OfferSettingsItemType.USER_SHORT,
      value: "_XX",
    } as OfferSettingsItem,
    prefix3: {
      type: OfferSettingsItemType.DATE,
      value: MultiSelectValue.DDMMYYYY,
    } as OfferSettingsItem,    
    suffix: {
      type: OfferSettingsItemType.VERSION_NUMBER,
      value: "-01",
    } as OfferSettingsItem,
  };

  constructor(private storeService: StoreService) {
    this.initSettings();
  }

  private initSettings(): void {
    this.storeService.getAccountProfile().subscribe((accountProfile) => {
      this.userName = {
        firstName: accountProfile?.firstName ?? "",
        lastName: accountProfile?.lastName ?? "",
      };
    });
  }

  // creates an abbreviation out of the prefix setting: e.g. setting = _Xy and Name Hans Meier => _Hm
  getAbbreviation(prefix: OfferSettingsItem, offer?: Partial<Offer>): string {

    const offerProfile = offer?.snkCreator?.profile;
    
    let firstName = offerProfile ? offerProfile.firstName : this.userName.firstName;
    let lastName = offerProfile ? offerProfile.lastName : this.userName.lastName;
    let out = "";
    if (!firstName || !lastName) {
      return prefix.value;
    }
    switch (prefix.value) {
      case "xy":
        out = firstName[0] + lastName[0];
        break;
      case "XY":
        out =
          firstName[0].toUpperCase() +
          lastName[0].toUpperCase();
        break;
      case "xY":
        out = firstName[0] + lastName[0].toUpperCase();
        break;
      case "XYy":
        out =
          firstName[0].toUpperCase() +
          lastName.charAt(0).toUpperCase() + lastName.slice(1, 2);
        break;
    }

    return out;
  }

  getSections(input: string): string[] {
    const cleanedInput = input.replace(/^[-_]+/, "");

    const result = cleanedInput.match(/[A-Za-z][a-z]*/g);

    return result ? result : [];
  }

  getRevisionSuffix(revision: number, suffix?: string): string {
    // removes last character
    const newSuffix = suffix ? suffix.trim().slice(0, -1) : "";

    return newSuffix + revision.toString();
  }

  generateOfferTitle(
    offer: Partial<Offer>,
    customSettings?: Partial<OfferSettings>,
    skipOfferNoIncrement = false
  ): string {
    const offerNr = (offer && offer.id) || "";
    const objectNr = (offer && offer.object && offer.object.id) || "";
    const customerName = (offer && offer.customer && offer.customer.name) || "";
    const customerNr = (offer && offer.customer && offer.customer.id) || "";
    const date = (offer && offer.createdDate) || new Date().toISOString();
    const revision = ((offer && offer.revision) || 0) + 1;

    let offerSettingsToUse: Partial<OfferSettings> = this.defaultOfferSettings;
    if (
      customSettings &&
      customSettings.prefix1 &&
      customSettings.prefix2 &&
      customSettings.suffix
    ) {
      offerSettingsToUse = customSettings;
    }

    const [year, month, day] = date.split("T")[0].split("-");

    // TODO: can be discuss or/and remove after Bau
    const [hours, minutes, seconds] = date
      .split("T")[1]
      .split(".")[0]
      .split(":");

    let offerTitle = "";

    const prefixes = [
      offerSettingsToUse.prefix1,
      offerSettingsToUse.prefix2,
      offerSettingsToUse.prefix3,
    ];

    prefixes.forEach(async (prefix: OfferSettingsItem | undefined, index: number) => {

      if (!prefix) return;

      switch (prefix.type) {
        case OfferSettingsItemType.DATE:
          switch (prefix.value) {
            case MultiSelectValue.YY:
              offerTitle += year.slice(-2);
              break;
            case MultiSelectValue.YYYY:
              offerTitle += year;
              break;
            case MultiSelectValue.MMYYYY:
              offerTitle += `${month}${year}`;
              break;
            case MultiSelectValue.DDMMYYYY:
              offerTitle += `${day}${month}${year}`;
              break;
            case MultiSelectValue.HHMMSS:
              offerTitle += `${Number(hours)}${minutes}${seconds}`;
              break;
            default:
              break;
          }
          break;
        case OfferSettingsItemType.USER_SHORT:
          offerTitle += this.getAbbreviation(prefix, offer);
          break;
        case OfferSettingsItemType.OFFER_DATA:
          switch (prefix.value) {
            case MultiSelectValue.OFFER_NR:
              offerTitle += offerNr || DEFAULT_VALUES.OFFER_NR;
              break;
            case MultiSelectValue.OBJECT_NR:
              offerTitle += objectNr || DEFAULT_VALUES.OBJECT_NR;
              break;
            default:
              break;
          }
          break;
        case OfferSettingsItemType.CUSTOMER_DATA:
          switch (prefix.value) {
            case MultiSelectValue.CUSTOMER_NAME:
              offerTitle += customerName || DEFAULT_VALUES.CUSTOMER.NAME;
              break;
            case MultiSelectValue.CUSTOMER_NR:
              offerTitle += customerNr || DEFAULT_VALUES.CUSTOMER.NR;
              break;
            default:
              break;
          }
          break;
        case OfferSettingsItemType.FREE_TEXT:
          offerTitle += prefix.value;
          break;
        case OfferSettingsItemType.OFFER_NUMBER: {
          const prefixes = [customSettings?.prefix1, customSettings?.prefix2, customSettings?.prefix3];
          const match = prefixes.find((p) => p?.type === prefix.type);
          const currentCounterValue =
            String(Number(match?.storedValue) + (skipOfferNoIncrement ? 0 : 1)) ?? "1";
          offerTitle += currentCounterValue.padStart(prefix.value.length, "0");
          break;
        }
        default:
          break;
      }

      if (index < 2 && prefixes[index + 1]) {
        offerTitle += '-';
      }
    });

    offerTitle += this.getRevisionSuffix(
      revision,
      offerSettingsToUse.suffix?.value ||
        this.defaultOfferSettings.suffix?.value
    );

    return offerTitle;
  }
}
